import { EnumHelper } from '@/helpers/enumHelper';
import { AlertService } from '@/services/alert.service';
import { formatDate, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import {
  ErrorBusinessCode,
  PropertySale,
  PropertySaleStatus,
  SalesManagementService,
  SellingStatus
} from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { CompanyDialogComponent } from '../company-dialog/company-dialog.component';

import { SelectComponent } from '../ui-forms/select/select.component';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';
import { InputComponent } from '../ui-forms/input/input.component';

export class SaleUpdateStatusDialogData {
  sale: PropertySale;
}

@Component({
    selector: 'app-sale-update-status-dialog',
    templateUrl: './sale-update-status-dialog.component.html',
    styleUrls: ['./sale-update-status-dialog.component.scss'],
    standalone: true,
    imports: [ModalBaseComponent, NgTemplateOutlet, MatDialogModule, FormsModule, ReactiveFormsModule, SelectComponent, InputComponent]
})
export class SaleUpdateStatusDialogComponent implements OnInit, OnDestroy {
  sub = new Subscription();
  actionSub = new Subscription();
  dialogTitle: string;
  isLoading = false;

  statusList: SellingStatus[] = EnumHelper.getApiEnumList(SellingStatus);
  disabledStatus: SellingStatus[] = [];

  get form_status() {
    return this.form.get('status') as FormControl<SellingStatus>;
  }
  get form_date() {
    return this.form.get('date') as FormControl<string>;
  }

  form = this.fb.group({
    status: [SellingStatus.SELLING, Validators.required],
    date: [formatDate(new Date(), 'yyyy-MM-dd', this.locale), Validators.required]
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SaleUpdateStatusDialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<CompanyDialogComponent>,
    private salesService: SalesManagementService,
    private alertService: AlertService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.dialogTitle = 'saleStatusDialog.titleUpdate';
  }

  ngOnInit(): void {
    this.initForm();
    this.initDisabledStatus();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.actionSub.unsubscribe();
  }

  initForm() {
    this.form_status.setValue(this.getNextStatus(this.data.sale.status));

    const statusDate = this.getDateFormStatus(this.data.sale.status);
    if (statusDate) this.form_date.setValue(statusDate);
  }

  getNextStatus(status: SellingStatus) {
    switch (status) {
      case 'SELLING':
        return SellingStatus.UNDER_OFFER;
      case 'UNDER_OFFER':
        return SellingStatus.UNDER_AGREEMENT;
      case 'UNDER_AGREEMENT':
        return SellingStatus.BANK_LOAN_ACCEPTED;
      case 'BANK_LOAN_ACCEPTED':
        return SellingStatus.READY_TO_SIGN;
      case 'READY_TO_SIGN':
        return SellingStatus.SOLD;
      default:
        return SellingStatus.CANCELED;
    }
  }

  getDateFormStatus(status: SellingStatus): string {
    switch (status) {
      case 'UNDER_OFFER':
        return this.data.sale.dateOffer;
      case 'UNDER_AGREEMENT':
        return this.data.sale.dateAgreement;
      case 'BANK_LOAN_ACCEPTED':
        return this.data.sale.dateBankLoanObtaining;
      case 'READY_TO_SIGN':
        return this.data.sale.dateSigning;
      case 'SOLD':
        return this.data.sale.dateSigning;
      default:
        return null;
    }
  }

  initDisabledStatus() {
    //SELLING and the current status always disabled
    this.disabledStatus.push(SellingStatus.SELLING);
    this.disabledStatus.push(this.data.sale.status);
    switch (this.data.sale.status) {
      case 'SOLD':
      case 'CANCELED':
        //all status disabled
        this.disabledStatus = this.statusList;
      case 'UNDER_OFFER':
        break;
      case 'READY_TO_SIGN':
        this.disabledStatus.push(SellingStatus.BANK_LOAN_ACCEPTED);
      case 'BANK_LOAN_ACCEPTED':
        this.disabledStatus.push(SellingStatus.UNDER_AGREEMENT);
      case 'UNDER_AGREEMENT':
        this.disabledStatus.push(SellingStatus.UNDER_OFFER);
    }
  }

  statusChanged(status: SellingStatus) {
    let statusDate = this.getDateFormStatus(status);
    if (!statusDate) statusDate = formatDate(new Date(), 'yyyy-MM-dd', this.locale);
    this.form_date.setValue(statusDate);
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.update();
    }
  }

  update() {
    this.isLoading = true;
    const body = this.getCreateOrUpdateBody();
    this.actionSub = this.salesService.updateSaleStatus(this.data.sale.uid, body).subscribe({
      next: () => {
        this.alertService.success('Statut de la vente mis à jour');
        this.dialogRef.close(body.status);
      },
      error: (err: HttpErrorResponse) => {
        if (err.error && err.error.businessCode) {
          switch (err.error.businessCode) {
            case ErrorBusinessCode.INVALID_DATE:
              // parse the date between brackets
              const afterDate = formatDate(
                new Date((err.error.details as string).split('(')[1].replace(')', '')),
                'dd-MM-yyyy',
                this.locale
              );
              this.form_date.setErrors({ invalidDateAfter: afterDate });
              break;
          }
        }
        this.isLoading = false;
      }
    });
  }

  getCreateOrUpdateBody(): PropertySaleStatus {
    const status = { ...this.form.value };
    return status;
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }
}
